import React from 'react';
import { useLocation, useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import PagingComponent, { PagedState } from '../utils/paging';
import ActivityLogCell from './ActivityLogCell';
import { ActivityLog } from './activityLogs';
import { ManagerNickName } from '../managers/managers';
import {
    defaultVendorSortingOrder,
    defaultVendorUsersSortingOrder,
    VendorNickName,
    VendorUserName,
} from '../vendors/vendors';
import { AuthState } from '../login/auth';
import FeatureButton from '../utils/FeatureButton';
import { SORT_OPTIONS } from '../utils/hooks/useSort';
import { ManagerPermission } from '../permission-profiles/permission';
import {
    defaultPartnerSortOptions,
    defaultPartnerUserSortOptions,
    PartnerName,
    PartnerUserName,
} from '../partners/partners';

interface Props {
    loadActivityLogs: (page: number, limit: number, managerId?: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    activityLogs: PagedState<ActivityLog<any>>;
    managersNickNames: ManagerNickName[];
    vendorsNickNames: VendorNickName[];
    vendorUsersNames: VendorUserName[];
    isManager: boolean;
    entityId: string;
    url?: string;
    loadManagersNickNames: () => void;
    loadVendorsNickNames: (sortOptions: SORT_OPTIONS) => void;
    loadVendorUsersNames: (sortOptions: SORT_OPTIONS) => void;
    loadPartnersNames: (sortOptions: SORT_OPTIONS) => void;
    loadPartnerUserNames: (sortOptions: SORT_OPTIONS) => void;
    auth: AuthState;
    downloadRestriction: ManagerPermission[];
    partnersNickNames: Array<PartnerName>;
    partnerUsersNickNames: Array<PartnerUserName>;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface State {}

const PageLimit = 25;
class ActivityLogsList extends React.Component<Props, State> {
    componentDidMount() {
        this.props.loadManagersNickNames();
        this.props.loadVendorUsersNames(defaultVendorUsersSortingOrder);
        this.props.loadVendorsNickNames(defaultVendorSortingOrder);
        this.props.loadPartnersNames(defaultPartnerSortOptions);
        this.props.loadPartnerUserNames(defaultPartnerUserSortOptions);
        this.onChangePageRequested(1);
    }

    onChangePageRequested(page: number) {
        if (this.props.isManager) {
            this.props.loadActivityLogs(page, PageLimit, this.props.auth.manager.id);
        } else {
            this.props.loadActivityLogs(page, PageLimit);
        }
    }

    checkIfEntitiesListsAreFetched() {
        return (
            this.props.managersNickNames &&
            this.props.vendorsNickNames &&
            this.props.vendorUsersNames &&
            this.props.managersNickNames.length > 0 &&
            this.props.vendorsNickNames.length > 0
        );
    }
    render() {
        const manager = this.props.auth.manager;
        const downloadRestriction = this.props.downloadRestriction;
        return (
            <div>
                <div className="row">
                    <h6 className="col-sm-4">Activity Logs</h6>

                    {this.props.url && (
                        <div className="col-sm-8 tr">
                            <FeatureButton
                                linkTo={this.props.url}
                                manager={manager}
                                restrictedToFeatures={downloadRestriction}
                                state={{ entityId: this.props.entityId }}
                                title={'Download'}
                            />
                        </div>
                    )}
                </div>

                {this.checkIfEntitiesListsAreFetched() &&
                    this.props.activityLogs.items &&
                    this.props.activityLogs.items.length > 0 &&
                    this.props.activityLogs.items.map((log) => (
                        <ActivityLogCell
                            key={log.id}
                            log={log}
                            managersNickName={this.props.managersNickNames}
                            vendorsNickNames={this.props.vendorsNickNames}
                            vendorUsersNames={this.props.vendorUsersNames}
                            partnersNames={this.props.partnersNickNames}
                            partnerUsersNames={this.props.partnerUsersNickNames}
                        />
                    ))}
                <PagingComponent
                    onPageChanged={this.onChangePageRequested.bind(this)}
                    paging={this.props.activityLogs.paging}
                />
            </div>
        );
    }
}

const withHooks = (Component: any) => {
    // eslint-disable-next-line react/display-name
    return (props: any) => {
        const location = useLocation();
        const params = useParams();
        const navigate = useNavigate();
        const dispatch = useDispatch();
        const outletContext = useOutletContext<{
            generalInfoProps?: any;
            activityLogsProps?: Props;
            paymentProps?: any;
            panelProps?: any;
            branchesProps?: any;
            productProps?: any;
            vendorGeneralInfo?: any;
        }>();

        // Use props from outlet context if available (for nested routes), otherwise use passed props
        const finalProps = outletContext?.activityLogsProps || props;

        return <Component {...finalProps} dispatch={dispatch} location={location} navigate={navigate} params={params} />;
    };
};

export default withHooks(ActivityLogsList);
